import type { SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { maybeShowPushBanner } from './push';

interface Machine {
  id: string;
  code: string;
  name: string;
  description: string;
  price_cents: number;
  cycle_duration_sec: number;
  warning_window_sec: number;
}
interface User { id: string; email: string; wallet_balance_cents: number; }

const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const fmtClock = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

interface RenderState {
  user: User;
  machine: Machine;
  txStatus?: string;
  txStartedAt?: string;
}

export async function renderMachinePage(supabase: SupabaseClient, machineCode: string) {
  const app = document.getElementById('app')!;
  app.innerHTML = '<div id="loading">Loading…</div>';

  const userAuthResult = await supabase.auth.getUser();
  if (!userAuthResult.data.user) { app.innerHTML = '<div class="card error">Not signed in</div>'; return; }
  const userId = userAuthResult.data.user.id;

  const [{ data: machine, error: mErr }, { data: user, error: uErr }] = await Promise.all([
    supabase.from('machines').select('id, code, name, description, price_cents, cycle_duration_sec, warning_window_sec').eq('code', machineCode).single(),
    supabase.from('users').select('id, email, wallet_balance_cents').eq('id', userId).single(),
  ]);
  if (mErr || !machine) { app.innerHTML = `<div class="card error">Machine not found: ${mErr?.message ?? ''}</div>`; return; }
  if (uErr || !user)    { app.innerHTML = `<div class="card error">User load failed: ${uErr?.message ?? ''}</div>`; return; }

  let activeTxId: string | null = null;
  let txChannel: RealtimeChannel | null = null;
  let countdownTimer: number | null = null;

  const renderHeader = (u: User) => `
    <header class="app-header">
      <img class="logo" src="/hainsworth-logo.png" alt="Hainsworth Laundry">
      <div class="balance-pill"><span class="label">Wallet</span> ${fmtMoney(u.wallet_balance_cents)}</div>
    </header>
  `;

  const renderCycleActive = (state: RenderState) => {
    const { txStatus, txStartedAt, machine: m } = state;
    if (!txStatus) return null;

    if (txStatus === 'paid') {
      return `
        <div class="status-banner paid">
          <div class="icon">⟳</div>
          <div class="label">Starting</div>
          <div class="msg">Sending command to your machine…</div>
        </div>
      `;
    }

    const elapsed = txStartedAt ? Math.max(0, (Date.now() - new Date(txStartedAt).getTime()) / 1000) : 0;
    const remaining = Math.max(0, Math.floor(m.cycle_duration_sec - elapsed));

    if (txStatus === 'running') {
      return `
        <div class="status-banner running">
          <div class="icon">⟳</div>
          <div class="label">Washing</div>
          <div class="countdown" data-countdown="${m.cycle_duration_sec}" data-started="${txStartedAt ?? ''}">${fmtClock(remaining)}</div>
          <div class="msg">remaining</div>
        </div>
      `;
    }
    if (txStatus === 'warning') {
      return `
        <div class="status-banner warning">
          <div class="icon">⚠</div>
          <div class="label">Almost done</div>
          <div class="countdown" data-countdown="${m.cycle_duration_sec}" data-started="${txStartedAt ?? ''}">${fmtClock(remaining)}</div>
          <div class="msg">grab your laundry soon</div>
        </div>
      `;
    }
    if (txStatus === 'completed') {
      return `
        <div class="status-banner completed">
          <div class="icon">✓</div>
          <div class="label">Complete</div>
          <div class="msg" style="font-size:18px;font-weight:700;margin-top:4px;">Your laundry is ready</div>
        </div>
      `;
    }
    if (txStatus === 'failed_hub' || txStatus === 'failed') {
      return `
        <div class="status-banner failed">
          <div class="icon">✕</div>
          <div class="label">Cycle failed</div>
          <div class="msg">${fmtMoney(m.price_cents)} refunded to your wallet.</div>
        </div>
      `;
    }
    return null;
  };

  const render = (state: RenderState) => {
    if (countdownTimer !== null) { clearInterval(countdownTimer); countdownTimer = null; }

    const banner = renderCycleActive(state);
    const showAction = !banner; // Hide pay/topup while a cycle is in flight or done.
    const insufficient = state.user.wallet_balance_cents < state.machine.price_cents;

    app.innerHTML = `
      ${renderHeader(state.user)}
      <div class="card">
        <div class="machine-title">${state.machine.name}</div>
        <div class="machine-sub">${state.machine.description}</div>
        <div class="price-label" style="margin-top:20px;">Price</div>
        <div class="price">${fmtMoney(state.machine.price_cents)}</div>
        ${showAction ? `
          <div class="btn-stack" style="margin-top:24px;">
            ${insufficient
              ? `<button id="topup" class="primary">Add funds</button>
                 <button id="pay" class="secondary" disabled>Pay ${fmtMoney(state.machine.price_cents)}</button>
                 <p class="tiny" style="text-align:center;margin-top:6px;">Top up first — your wallet has ${fmtMoney(state.user.wallet_balance_cents)}</p>`
              : `<button id="pay" class="primary">Pay ${fmtMoney(state.machine.price_cents)}</button>
                 <button id="topup" class="secondary">Add funds</button>`}
          </div>
        ` : banner}
      </div>
    `;

    document.getElementById('pay')?.addEventListener('click', () => onPay(state.machine.code));
    document.getElementById('topup')?.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('open-topup', {
        detail: { suggestCents: insufficient ? state.machine.price_cents : undefined },
      }));
    });

    // Live countdown for running/warning banners
    const countdownEl = document.querySelector<HTMLElement>('.countdown');
    if (countdownEl) {
      const startedAt = countdownEl.dataset.started;
      const total = Number(countdownEl.dataset.countdown);
      if (startedAt && total) {
        const startMs = new Date(startedAt).getTime();
        countdownTimer = window.setInterval(() => {
          const remaining = Math.max(0, Math.floor(total - (Date.now() - startMs) / 1000));
          countdownEl.textContent = fmtClock(remaining);
          if (remaining <= 0 && countdownTimer !== null) {
            clearInterval(countdownTimer); countdownTimer = null;
          }
        }, 500);
      }
    }
  };

  const onPay = async (code: string) => {
    const payBtn = document.getElementById('pay') as HTMLButtonElement | null;
    if (payBtn) { payBtn.disabled = true; payBtn.textContent = 'Processing…'; }
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/pay', {
      method: 'POST',
      headers: { Authorization: `Bearer ${session!.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ machine_code: code }),
    });
    const result = await res.json() as any;
    if (!res.ok) {
      if (result.error === 'insufficient_funds') {
        window.dispatchEvent(new CustomEvent('open-topup', { detail: { suggestCents: result.need_topup_cents } }));
        if (payBtn) { payBtn.disabled = false; payBtn.textContent = `Pay ${fmtMoney(machine.price_cents)}`; }
        return;
      }
      alert('Payment failed: ' + result.error);
      if (payBtn) { payBtn.disabled = false; payBtn.textContent = `Pay ${fmtMoney(machine.price_cents)}`; }
      return;
    }
    user.wallet_balance_cents = result.new_balance_cents;
    activeTxId = result.transaction_id;
    render({ user, machine, txStatus: 'paid' });
    subscribeToTx(activeTxId!);
  };

  const subscribeToTx = (txId: string) => {
    if (txChannel) supabase.removeChannel(txChannel);
    txChannel = supabase.channel(`tx-${txId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'transactions', filter: `id=eq.${txId}` },
        async (payload) => {
          const tx = payload.new as any;
          render({ user, machine, txStatus: tx.status, txStartedAt: tx.cycle_started_at });
          if (tx.status === 'running' && !localStorage.getItem('first_payment_made')) {
            localStorage.setItem('first_payment_made', '1');
            setTimeout(() => maybeShowPushBanner(supabase), 1500);
          }
          if (['completed', 'failed_hub'].includes(tx.status)) {
            const { data } = await supabase.from('users').select('wallet_balance_cents').eq('id', user.id).single();
            if (data) {
              user.wallet_balance_cents = data.wallet_balance_cents;
              render({ user, machine, txStatus: tx.status, txStartedAt: tx.cycle_started_at });
            }
          }
        }
      )
      .subscribe();
  };

  // Subscribe to user balance updates from Stripe webhook
  supabase.channel(`user-${user.id}`)
    .on('postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'users', filter: `id=eq.${user.id}` },
      (payload) => {
        user.wallet_balance_cents = (payload.new as any).wallet_balance_cents;
        render({ user, machine });
      }
    )
    .subscribe();

  render({ user, machine });
}
